import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../../components/DataTable'
import Icon from '../../components/Icon'
import { useGoalsStore } from '../../../stores/goalsStore'
import { useAuth } from '../../../contexts/AuthContext'
import { Goal, Permission } from '../../../types'

interface StudentGoalsTabProps {
  studentId?: string
}

const StudentGoalsTab: React.FC<StudentGoalsTabProps> = ({ studentId }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { goals, loading, error, fetchGoalsByStudent, removeGoal } =
    useGoalsStore()

  // Fetch goals when component mounts or studentId changes
  useEffect(() => {
    if (studentId) {
      fetchGoalsByStudent(studentId)
    }
  }, [studentId, fetchGoalsByStudent])

  const handleDeleteGoal = async (id: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete this goal? This action cannot be undone.`,
      )
    ) {
      try {
        await removeGoal(id)
        if (studentId) {
          await fetchGoalsByStudent(studentId)
        }
      } catch (err) {
        console.error('Error deleting goal:', err)
      }
    }
  }

  // Check if user has permission to manage students
  const canManageStudent = user?.permissions.includes(
    Permission.MANAGE_GOALS,
  )

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<Goal>[]>(
    () => [
      {
        accessorKey: 'category',
        header: 'Category',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {(info.getValue() as string) || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'area',
        header: 'Area',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {(info.getValue() as string) || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {(info.getValue() as string) || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'objectives',
        header: 'Objectives',
        cell: (info) => {
          const objectives = info.getValue() as string[]
          return (
            <div className="text-sm text-gray-500">
              {objectives.length === 0
                ? 'None'
                : objectives.length === 1
                  ? '1 objective'
                  : `${objectives.length} objectives`}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium flex justify-end gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigate(
                  `/manage/students/${studentId}/goals/${row.original._id}`,
                )
              }}
              className="text-blue-600 hover:text-blue-900"
            >
              Edit
            </button>
            {canManageStudent && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteGoal(row.original._id)
                }}
                className="text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            )}
          </div>
        ),
      },
    ],
    [studentId, canManageStudent, navigate, fetchGoalsByStudent],
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
          <p className="text-gray-600 text-sm">Manage goals for this student</p>
        </div>
        {canManageStudent && (
          <button
            onClick={() => navigate(`/manage/students/${studentId}/goals/new`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Icon type="plus" className="w-5 h-5" />
            Add Goal
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Goals Table */}
      <DataTable
        data={goals}
        columns={columns}
        searchPlaceholder="Search goals..."
        emptyStateTitle="No goals"
        emptyStateDescription="Get started by adding your first goal."
        emptyStateIcon={
          <Icon type="bolt" className="mx-auto h-12 w-12 text-gray-400" />
        }
        onCreateClick={
          canManageStudent
            ? () => navigate(`/manage/students/${studentId}/goals/new`)
            : undefined
        }
        createButtonText="Add Goal"
        showCreateButton={canManageStudent}
        loading={loading}
        onRowClick={(goal) =>
          navigate(`/manage/students/${studentId}/goals/${goal._id}`)
        }
      />
    </div>
  )
}

export default StudentGoalsTab
